import {
  ActionTypes as FeedItemActionTypes,
  IArchiveLeadSuccessAction,
} from '@app/components/FeedItem/modules/feedItems'
import {
  ActionTypes as SignTransferActionTypes,
  IDeleteSignTransferSuccessAction,
  ISaveSignTransferSuccessAction,
} from '@app/components/SignTransferModal/modules/signTransfer'
import { Assignment, IAssignment } from '@app/models/Assignment'
import { IAttachment } from '@app/models/Attachment'
import {
  EntityPermissions,
  IEntityPermissions,
} from '@app/models/EntityPermissions'
import { IProperty, Property } from '@app/models/Property'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import {
  IRequestOptions,
  makeAuthenticatedRequest,
  RequestMethod,
} from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------

export interface IPropertyState {
  readonly assignments: List<IAssignment>
  readonly error?: string
  readonly includeArchivedLeads: boolean
  readonly includeCompletedAssignments: boolean
  readonly isDeleting: boolean
  readonly isLoading: boolean
  readonly isLoadingAssignments: boolean
  readonly permissions: IEntityPermissions
  readonly property: IProperty
  readonly showConfirmDelete: boolean
}

const PropertyStateRecord = Record({
  assignments: List<Assignment>(),
  error: undefined,
  includeArchivedLeads: false,
  includeCompletedAssignments: false,
  isDeleting: false,
  isLoading: false,
  isLoadingAssignments: false,
  permissions: new EntityPermissions(),
  property: new Property(),
  showConfirmDelete: false,
})

export class PropertyState extends PropertyStateRecord
  implements IPropertyState {
  public static fromJS(initialState?: Partial<IPropertyState>) {
    if (!initialState) {
      return new PropertyState()
    }

    const { assignments, property, permissions, ...rest } = initialState
    return new PropertyState({
      assignments: assignments
        ? List(assignments.map((a) => Assignment.fromJS(a)))
        : List<Assignment>(),
      permissions: EntityPermissions.fromJS(permissions),
      property: Property.fromJS(property),
      ...rest,
    })
  }

  public readonly assignments: List<Assignment>
  public readonly error?: string
  public readonly includeArchivedLeads: boolean
  public readonly includeCompletedAssignments: boolean
  public readonly isDeleting: boolean
  public readonly isLoading: boolean
  public readonly isLoadingAssignments: boolean
  public readonly permissions: EntityPermissions
  public readonly property: Property
  public readonly showConfirmDelete: boolean

  constructor(props?: Partial<PropertyState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  DeleteProperty = 'PROPERTY::DELETE_PROPERTY',
  DeletePropertyFailure = 'PROPERTY::DELETE_PROPERTY_FAILURE',
  DeletePropertyRequest = 'PROPERTY::DELETE_PROPERTY_REQUEST',
  DeletePropertySuccess = 'PROPERTY::DELETE_PROPERTY_SUCCESS',
  LoadAssignments = 'PROPERTY::LOAD_ASSIGNMENTS',
  LoadAssignmentsFailure = 'PROPERTY::LOAD_ASSIGNMENTS_FAILURE',
  LoadAssignmentsRequest = 'PROPERTY::LOAD_ASSIGNMENTS_REQUEST',
  LoadAssignmentsSuccess = 'PROPERTY::LOAD_ASSIGNMENTS_SUCCESS',
  LoadProperty = 'PROPERTY::LOAD',
  LoadPropertyFailure = 'PROPERTY::LOAD_FAILURE',
  LoadPropertyRequest = 'PROPERTY::LOAD_REQUEST',
  LoadPropertySuccess = 'PROPERTY::LOAD_SUCCESS',
  SetAttachments = 'PROPERTY::SET_ATTACHMENTS',
  SetIncludeArchived = 'PROPERTY::SET_INCLUDE_ARCHIVED',
  SetIncludeCompletedAssignments = 'PROPERTY::SET_INCLUDE_COMPLETED_ASSIGNMENTS',
  ToggleConfirmDelete = 'PROPERTY::TOGGLE_CONFIRM_DELETE',
  ToggleFavorite = 'PROPERTY::TOGGLE_FAVORITE',
  ToggleFavoriteSuccess = 'PROPERTY::TOGGLE_FAVORITE_SUCCESS',
}

interface IDeletePropertyAction {
  propertyId: string
  type: ActionTypes.DeleteProperty
}

interface IDeletePropertyFailureAction {
  error: string
  type: ActionTypes.DeletePropertyFailure
}

interface IDeletePropertyRequestAction {
  type: ActionTypes.DeletePropertyRequest
}

interface IDeletePropertySuccessAction {
  type: ActionTypes.DeletePropertySuccess
}

interface ILoadAssignmentsAction {
  includeCompleted: boolean
  propertyId: string
  type: ActionTypes.LoadAssignments
}

interface ILoadAssignmentsFailureAction {
  error: string
  type: ActionTypes.LoadAssignmentsFailure
}

interface ILoadAssignmentsRequestAction {
  type: ActionTypes.LoadAssignmentsRequest
}

interface ILoadAssignmentsSuccessAction {
  assignments: Assignment[]
  type: ActionTypes.LoadAssignmentsSuccess
}

interface ILoadPropertyAction {
  propertyId: string
  type: ActionTypes.LoadProperty
}

interface ILoadPropertyFailureAction {
  error: string
  type: ActionTypes.LoadPropertyFailure
}

interface ILoadPropertyRequestAction {
  type: ActionTypes.LoadPropertyRequest
}

interface ILoadPropertySuccessAction {
  permissions: EntityPermissions
  property: Property
  type: ActionTypes.LoadPropertySuccess
}

interface ISetAttachmentsAction {
  type: ActionTypes.SetAttachments
  value: List<IAttachment>
}

interface ISetIncludeArchivedAction {
  includeArchived: boolean
  type: ActionTypes.SetIncludeArchived
}

interface ISetIncludeCompletedAssignmentsAction {
  includeCompleted: boolean
  type: ActionTypes.SetIncludeCompletedAssignments
}

interface IToggleConfirmDeleteAction {
  show: boolean
  type: ActionTypes.ToggleConfirmDelete
}

interface IToggleFavoriteAction {
  isFavorite: boolean
  propertyId: string
  type: ActionTypes.ToggleFavorite
  userId: string
}

interface IToggleFavoriteSuccessAction {
  isFavorite: boolean
  propertyId: string
  type: ActionTypes.ToggleFavoriteSuccess
  userId: string
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IArchiveLeadSuccessAction
  | IDeletePropertyFailureAction
  | IDeletePropertyRequestAction
  | IDeletePropertySuccessAction
  | IDeleteSignTransferSuccessAction
  | ILoadAssignmentsFailureAction
  | ILoadAssignmentsRequestAction
  | ILoadAssignmentsSuccessAction
  | ILoadPropertyFailureAction
  | ILoadPropertyRequestAction
  | ILoadPropertySuccessAction
  | ISaveSignTransferSuccessAction
  | ISetAttachmentsAction
  | ISetIncludeArchivedAction
  | ISetIncludeCompletedAssignmentsAction
  | IToggleConfirmDeleteAction
  | IToggleFavoriteSuccessAction

// -------------------------
// ACTION CREATORS
// -------------------------
function deleteProperty(propertyId: string): IDeletePropertyAction {
  return {
    propertyId,
    type: ActionTypes.DeleteProperty,
  }
}

function deletePropertyFailure(error: string): IDeletePropertyFailureAction {
  return {
    error,
    type: ActionTypes.DeletePropertyFailure,
  }
}

function deletePropertyRequest(): IDeletePropertyRequestAction {
  return { type: ActionTypes.DeletePropertyRequest }
}

function deletePropertySuccess(): IDeletePropertySuccessAction {
  return { type: ActionTypes.DeletePropertySuccess }
}

function loadAssignments(
  propertyId: string,
  includeCompleted: boolean,
): ILoadAssignmentsAction {
  return {
    includeCompleted,
    propertyId,
    type: ActionTypes.LoadAssignments,
  }
}

function loadAssignmentsFailure(error: string): ILoadAssignmentsFailureAction {
  return {
    error,
    type: ActionTypes.LoadAssignmentsFailure,
  }
}

function loadAssignmentsRequest(): ILoadAssignmentsRequestAction {
  return {
    type: ActionTypes.LoadAssignmentsRequest,
  }
}

function loadAssignmentsSuccess(
  assignments: Assignment[],
): ILoadAssignmentsSuccessAction {
  return {
    assignments,
    type: ActionTypes.LoadAssignmentsSuccess,
  }
}

function loadProperty(propertyId: string): ILoadPropertyAction {
  return {
    propertyId,
    type: ActionTypes.LoadProperty,
  }
}

function loadPropertyFailure(error: string): ILoadPropertyFailureAction {
  return {
    error,
    type: ActionTypes.LoadPropertyFailure,
  }
}

function loadPropertyRequest(): ILoadPropertyRequestAction {
  return {
    type: ActionTypes.LoadPropertyRequest,
  }
}

function loadPropertySuccess(
  property: Property,
  permissions: EntityPermissions,
): ILoadPropertySuccessAction {
  return {
    permissions,
    property,
    type: ActionTypes.LoadPropertySuccess,
  }
}

function setAttachments(value: List<IAttachment>): ISetAttachmentsAction {
  return {
    type: ActionTypes.SetAttachments,
    value,
  }
}

function setIncludeArchived(
  includeArchived: boolean,
): ISetIncludeArchivedAction {
  return {
    includeArchived,
    type: ActionTypes.SetIncludeArchived,
  }
}

function setIncludeCompletedAssignments(
  includeCompleted: boolean,
): ISetIncludeCompletedAssignmentsAction {
  return {
    includeCompleted,
    type: ActionTypes.SetIncludeCompletedAssignments,
  }
}

function toggleConfirmDelete(show: boolean): IToggleConfirmDeleteAction {
  return {
    show,
    type: ActionTypes.ToggleConfirmDelete,
  }
}

function toggleFavorite(
  propertyId: string,
  userId: string,
  isFavorite: boolean,
): IToggleFavoriteAction {
  return {
    isFavorite,
    propertyId,
    type: ActionTypes.ToggleFavorite,
    userId,
  }
}

function toggleFavoriteSuccess(
  propertyId: string,
  userId: string,
  isFavorite: boolean,
): IToggleFavoriteSuccessAction {
  return {
    isFavorite,
    propertyId,
    type: ActionTypes.ToggleFavoriteSuccess,
    userId,
  }
}

export const actionCreators = {
  deleteProperty,
  loadAssignments,
  loadProperty,
  setAttachments,
  setIncludeArchived,
  setIncludeCompletedAssignments,
  toggleConfirmDelete,
  toggleFavorite,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<PropertyState> = (
  state: PropertyState = new PropertyState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case FeedItemActionTypes.ArchiveLeadSuccess:
      const leadIndex = state.property.leads.findIndex((item: any) => {
        return item.id === action.id
      })

      if (leadIndex > -1) {
        return state.setIn(
          ['property', 'leads', leadIndex, 'isArchived'],
          true,
        ) as PropertyState
      }

      return state

    case ActionTypes.DeletePropertyFailure:
      return state
        .set('error', action.error)
        .set('isDeleting', false) as PropertyState

    case ActionTypes.DeletePropertyRequest:
      return state.set('isDeleting', true) as PropertyState

    case ActionTypes.DeletePropertySuccess:
      return state
        .set('isDeleting', false)
        .set('property', new Property()) as PropertyState

    case ActionTypes.LoadAssignmentsFailure:
      return state
        .set('error', action.error)
        .set('isLoadingAssignments', false) as PropertyState

    case ActionTypes.LoadAssignmentsRequest:
      return state
        .set('assignments', List<Assignment>())
        .set('isLoadingAssignments', true) as PropertyState

    case ActionTypes.LoadAssignmentsSuccess:
      return state
        .set('assignments', List(action.assignments))
        .set('isLoadingAssignments', false) as PropertyState

    case ActionTypes.LoadPropertyFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as PropertyState

    case ActionTypes.LoadPropertyRequest:
      return state.set('isLoading', true) as PropertyState

    case ActionTypes.LoadPropertySuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('permissions', action.permissions)
        .set('property', action.property) as PropertyState

    case ActionTypes.SetAttachments:
      return state.setIn(
        ['property', 'attachments'],
        action.value,
      ) as PropertyState

    case ActionTypes.SetIncludeArchived:
      return state.set(
        'includeArchivedLeads',
        action.includeArchived,
      ) as PropertyState

    case ActionTypes.SetIncludeCompletedAssignments:
      return state.set(
        'includeCompletedAssignments',
        action.includeCompleted,
      ) as PropertyState

    case ActionTypes.ToggleConfirmDelete:
      return state.set('showConfirmDelete', action.show) as PropertyState

    case ActionTypes.ToggleFavoriteSuccess:
      if (state.property.id === action.propertyId) {
        let favorites = state.property.hasStarred
        if (action.isFavorite === false) {
          favorites = favorites.filter((f) => f !== action.userId).toList()
        } else if (!favorites.includes(action.userId)) {
          favorites = favorites.push(action.userId)
        }
        return state.setIn(
          ['property', 'hasStarred'],
          favorites,
        ) as PropertyState
      } else {
        return state
      }

    case SignTransferActionTypes.DeleteSignTransferSuccess: {
      if (state.property.id !== action.signTransfer.from.propertyId) {
        return state
      }

      const signIndex = state.property.signs.findIndex(
        (s) => s !== undefined && s.id === action.signTransfer.signId,
      )
      if (signIndex < 0) {
        return state
      }

      const transferIndex = state.property.signs
        .get(signIndex)
        .pendingTransfers.findIndex(
          (p) => p !== undefined && p.id === action.signTransfer.id,
        )
      if (transferIndex < 0) {
        return state
      }

      return state.deleteIn([
        'property',
        'signs',
        signIndex,
        'pendingTransfers',
        transferIndex,
      ]) as PropertyState
    }

    case SignTransferActionTypes.SaveSignTransferSuccess:
      if (state.property.id === action.signTransfer.from.propertyId) {
        const signIndex = state.property.signs.findIndex(
          (s) => s !== undefined && s.id === action.signTransfer.signId,
        )
        return state.setIn(
          ['property', 'signs', signIndex, 'pendingTransfers'],
          state.property.signs
            .get(signIndex)
            .pendingTransfers.push(action.signTransfer),
        ) as PropertyState
      } else {
        return state
      }

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* deletePropertySaga(action: IDeletePropertyAction) {
  try {
    yield put(deletePropertyRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/Properties/${action.propertyId}`,
      { method: RequestMethod.DELETE },
    )

    if (result.success === true) {
      yield put(push('/properties/properties'))
      yield put(deletePropertySuccess())
    } else {
      yield put(deletePropertyFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(deletePropertyFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadAssignmentsSaga(action: ILoadAssignmentsAction) {
  try {
    yield put(loadAssignmentsRequest())

    const url = `/api/Properties/${action.propertyId}/Assignments?includeCompleted=${action.includeCompleted}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      yield put(
        loadAssignmentsSuccess(
          result.results.map((a: any) => Assignment.fromJS(a)),
        ),
      )
    } else {
      yield put(loadAssignmentsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadAssignmentsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadPropertySaga(action: ILoadPropertyAction) {
  try {
    yield put(loadPropertyRequest())

    const url = `/api/Properties/${action.propertyId}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      yield put(
        loadPropertySuccess(
          Property.fromJS(result.entity),
          EntityPermissions.fromJS(result.permissions),
        ),
      )
    } else {
      yield put(loadPropertyFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadPropertyFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* toggleFavoriteSaga(action: IToggleFavoriteAction) {
  try {
    const url = `/api/Properties/my/${action.propertyId}`

    const opts: IRequestOptions = {
      method: action.isFavorite ? RequestMethod.POST : RequestMethod.DELETE,
    }

    const result = yield makeAuthenticatedRequest(url, opts)

    if (result.success === true) {
      yield put(
        toggleFavoriteSuccess(
          action.propertyId,
          action.userId,
          action.isFavorite,
        ),
      )
    } else {
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchDeleteProperty() {
  yield takeLatest(ActionTypes.DeleteProperty, deletePropertySaga)
}

function* watchLoadAssignments() {
  yield takeLatest(ActionTypes.LoadAssignments, loadAssignmentsSaga)
}

function* watchLoadProperty() {
  yield takeLatest(ActionTypes.LoadProperty, loadPropertySaga)
}

function* watchToggleFavorite() {
  yield takeLatest(ActionTypes.ToggleFavorite, toggleFavoriteSaga)
}

export function* propertyRootSaga() {
  yield fork(watchDeleteProperty)
  yield fork(watchLoadAssignments)
  yield fork(watchLoadProperty)
  yield fork(watchToggleFavorite)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) => state.property

export const selectAssignments = () =>
  createSelector(selectModuleState(), (mState) => mState.assignments)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIncludeArchived = () =>
  createSelector(selectModuleState(), (mState) => mState.includeArchivedLeads)

export const selectIncludeCompletedAssignments = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.includeCompletedAssignments,
  )

export const selectIsDeleting = () =>
  createSelector(selectModuleState(), (mState) => mState.isDeleting)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectIsLoadingAssignments = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoadingAssignments)

export const selectPermissions = () =>
  createSelector(selectModuleState(), (mState) => mState.permissions)

export const selectProperty = () =>
  createSelector(selectModuleState(), (mState) => mState.property)

export const selectShowConfirmDelete = () =>
  createSelector(selectModuleState(), (mState) => mState.showConfirmDelete)
